import { useEffect, useRef, useState } from "react";
import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { usePlaybackStore } from "../state/playbackStore";
import { analyzeFile, analyzeYouTube, pollYtJob } from "../api/server";
import type { Analysis } from "../types/domain";
import { UploadIcon, LinkIcon, OfflineIcon } from "./icons";
import { useT } from "../i18n";

type Phase = { kind: "idle" } | { kind: "analyzing"; label: string } | { kind: "error"; msg: string };

const MAX_FILE_MB = 100;   // keep in sync with server MAX_UPLOAD_MB + nginx client_max_body_size

export function SongInput() {
  const c = useControllers();
  const t = useT();
  const loadAnalysis = useProjectStore((s) => s.loadAnalysis);
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [url, setUrl] = useState("");
  const [drag, setDrag] = useState(false);

  const stopAll = () => { c.song.pause(); c.youtube.stop(); c.score.stop(); };

  // deep link from the SEO chord page: /?v=<videoId> → auto-load that analysis (cache hit, fast)
  useEffect(() => {
    const v = new URLSearchParams(window.location.search).get("v");
    if (v && /^[A-Za-z0-9_-]{11}$/.test(v)) {
      const yurl = `https://www.youtube.com/watch?v=${v}`;
      setUrl(yurl);
      handleYouTube(yurl);
      window.history.replaceState({}, "", window.location.pathname);   // drop ?v so refresh/back doesn't re-trigger
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(file: File) {
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setPhase({ kind: "error", msg: t("input.toobig") });
      return;
    }
    setPhase({ kind: "analyzing", label: t("input.analyzing") });
    try {
      const analysis = await analyzeFile(file);
      stopAll();
      c.song.load(URL.createObjectURL(file));
      loadAnalysis(analysis, { kind: "localFile", fileName: file.name }, file.name.replace(/\.[^.]+$/, ""));
      usePlaybackStore.getState().setPlayMode("song");
      setPhase({ kind: "idle" });
    } catch {
      setPhase({ kind: "error", msg: t("input.err") });
    }
  }

  async function handleYouTube(rawUrl?: string) {
    const u = (rawUrl ?? url).trim();
    if (!u) return;
    setPhase({ kind: "analyzing", label: t("input.analyzing_yt") });
    try {
      const res = await analyzeYouTube(u);
      let analysis: Analysis & { video_id: string };
      if ("queued" in res && res.queued) {
        setPhase({ kind: "analyzing", label: t("input.queued") });
        analysis = await pollYtJob(res.job_id, () => setPhase({ kind: "analyzing", label: t("input.analyzing_yt") }));
      } else {
        analysis = res as Analysis & { video_id: string };
      }
      stopAll();
      await c.youtube.load(analysis.video_id);
      loadAnalysis(analysis, { kind: "youtube", videoId: analysis.video_id }, "YouTube");
      usePlaybackStore.getState().setPlayMode("song");
      setPhase({ kind: "idle" });
    } catch {
      setPhase({ kind: "error", msg: t("input.err") });
    }
  }

  const online = typeof navigator === "undefined" || navigator.onLine;

  return (
    <section className="section">
      <h2>{t("input.title")}</h2>
      <div className="input-card">
        <div
          className={"drop" + (drag ? " over" : "")}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
        >
          <UploadIcon size={32} />
          <div className="drop-title">{t("input.drop")}</div>
          <div className="drop-sub">{t("input.formats")}</div>
          <input ref={fileRef} type="file" accept="audio/*,video/*" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.currentTarget.value = ""; }} />
        </div>

        <div className="or-divider"><span className="hr" /><span className="or">{t("input.or")}</span><span className="hr" /></div>

        <div className="url-row">
          <span className="url-ico"><LinkIcon size={16} /></span>
          <input className="url-input" type="text" inputMode="url" placeholder={t("input.yturl")}
            value={url} onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleYouTube(); }} />
          <button className="btn-pri" onClick={() => handleYouTube()} disabled={phase.kind === "analyzing"}>{t("input.analyze")}</button>
        </div>
        <div className="yt-hint">{t("input.ythint")}</div>

        {phase.kind === "analyzing" && (
          <div className="status analyzing"><span className="spinner" />{phase.label}</div>
        )}
        {phase.kind === "error" && <div className="status err">{phase.msg}</div>}
        {!online && (
          <div className="banner"><OfflineIcon size={16} /> {t("input.offline")}</div>
        )}
      </div>
    </section>
  );
}
